import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api";

function Team({ name, crest }) {
  return (
    <span className="flex items-center gap-1.5 min-w-0">
      {crest && <img src={crest} alt="" className="w-4 h-4 object-contain shrink-0" loading="lazy" />}
      <span className="truncate">{name}</span>
    </span>
  );
}

const dayTime = (iso) => new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", weekday: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
const kickoff = (iso) => new Date(iso).toLocaleTimeString("it-IT", { timeZone: "Europe/Rome", hour: "2-digit", minute: "2-digit" });

function MatchChip({ m }) {
  const live = m.status === "IN_PLAY" || m.status === "PAUSED";
  const next = m.status === "NEXT";
  const today = m.status === "TODAY" || next;
  return (
    <div
      data-testid={`score-${m.id}`}
      className={`snap-start shrink-0 w-[168px] rounded-xl border px-3 py-2.5 text-sm ${live ? "border-brand bg-brand/10" : "border-hair bg-surfaceSecondary"}`}
    >
      <div className={`flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider mb-1.5 ${live ? "text-brand" : "text-subtle"}`}>
        {live && <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" aria-hidden="true" />}
        {live ? (m.status === "PAUSED" ? "Intervallo" : m.minute ? `${m.minute}'` : "In corso") : next ? dayTime(m.utc_date) : today ? `Oggi ${kickoff(m.utc_date)}` : "Finale"}
      </div>
      {[["home", m.home, m.home_crest, m.home_score], ["away", m.away, m.away_crest, m.away_score]].map(([k, n, c, sc]) => (
        <div key={k} className="flex items-center justify-between gap-2 text-onSurface">
          <Team name={n} crest={c} />
          {!today && <span className="font-mono font-semibold tabular-nums">{sc ?? "–"}</span>}
        </div>
      ))}
    </div>
  );
}

export function SerieAScores() {
  const [data, setData] = useState(null);
  const [showPrev, setShowPrev] = useState(false);
  useEffect(() => {
    let alive = true;
    const load = () => apiFetch(`/serie-a/scores`, { auth: false }).then((r) => alive && setData(r)).catch(() => {});
    load();
    const t = window.setInterval(load, 60000);
    return () => { alive = false; window.clearInterval(t); };
  }, []);
  const rnd = data?.round;
  if (!data?.enabled || (!data.matches?.length && !rnd)) return null;
  // giornata finita (dal giorno dopo l'ultima partita): si mostra la successiva, i risultati restano con un tasto
  const list = rnd ? (showPrev ? rnd.previous.matches : rnd.matches.map((m) => ({ ...m, status: "NEXT" }))) : data.matches;
  return (
    <section data-testid="serie-a-scores" className="mb-6">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2.5">
        <h2 className="font-serif text-lg font-semibold text-onSurface">
          Serie A{rnd && <span data-testid="serie-a-round-label"> · {showPrev ? `Risultati ${rnd.previous.matchday}ª giornata` : `${rnd.matchday}ª giornata`}</span>}
        </h2>
        {rnd ? (
          <button type="button" data-testid="serie-a-round-toggle" onClick={() => setShowPrev((v) => !v)}
            className="text-xs font-semibold text-brand hover:underline">
            {showPrev ? `Vedi ${rnd.matchday}ª giornata` : `Risultati ${rnd.previous.matchday}ª giornata`}
          </button>
        ) : <span className="text-[11px] font-mono text-subtle">Risultati con qualche minuto di ritardo</span>}
      </div>
      <div className="-mx-4 px-4 sm:mx-0 sm:px-0 flex gap-3 overflow-x-auto scroll-px-4 snap-x pb-1 [scrollbar-width:none]">
        {list.map((m) => <MatchChip key={m.id} m={m} />)}
      </div>
    </section>
  );
}
